import { BaseTool, DragEventData } from './BaseTool';
import { IViewConfig } from './types';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from './ToolSettings';
import { ISubTool, SubToolInputKind } from './SubTool';
import type { ToolUndoRedoManager } from './tools/ToolUndoRedoManager';
import type { AnnotationKindAdapter } from './tools/implementations/line-tool/AnnotationKindAdapter';
import type { SelectionContextProvider } from './tools/implementations/line-tool/SelectionContextProvider';

/** Constructor type the mixin can be applied over (any BaseTool subclass). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseToolCtor = abstract new (...args: any[]) => BaseTool;

/**
 * Mixin that turns any `BaseTool` subclass into a host for a single `ISubTool`
 * (the shared `LineTool`, or the `MeasureSubTool`, etc.). Folds in what used to
 * be the standalone `SubToolHost` controller PLUS the per-host input-forwarding
 * boilerplate, so a concrete host shrinks to: pick a domain base, call
 * `initSubToolHosting(subtool, adapter, ctx)` in its constructor, and
 * (optionally) override the `interceptXxx` hooks for domain gestures (e.g.
 * surface camera-align).
 *
 * Why a mixin and not a base class: hosts must extend their domain base
 * (`SurfaceToolBase` / `FloorplanToolBase`) for isolation/alignment, and TS is
 * single-inheritance — so hosting is mixed in over that base.
 *
 * The host owns DOM input + drag detection (via `BaseTool`); this mixin forwards
 * already-detected input to the subtool, filtered by the subtool's input mask
 * minus any kinds the host has muted. Subclasses intercept first via the
 * `interceptXxx` hooks (return true to swallow).
 */
export function SubToolHosting<TBase extends BaseToolCtor>(Base: TBase) {
  abstract class SubToolHostMixin extends Base {
    /** The hosted subtool, set by `initSubToolHosting`. */
    protected subtool!: ISubTool;
    /** The kind adapter driving the subtool (commit/view config). */
    protected kindAdapter!: AnnotationKindAdapter;

    private selectionContext!: SelectionContextProvider;
    private mutedInput = new Set<SubToolInputKind>();

    /**
     * Wire the subtool + adapter + selection context. Call once from the
     * concrete host's constructor.
     */
    protected initSubToolHosting(subtool: ISubTool, adapter: AnnotationKindAdapter, ctx: SelectionContextProvider): void {
      this.subtool = subtool;
      this.kindAdapter = adapter;
      this.selectionContext = ctx;
    }

    // ─── Subtool lifecycle ──────────────────────────────────────────────
    // Owned by the mixin: it chains `super` (the domain base does its
    // isolation/alignment) then activates/deactivates the subtool. Concrete
    // hosts don't repeat this; they only override the domain hooks
    // (onSurfaceChanged / onFloorChanged) to re-push the base plane.

    protected onEnable(): void {
      super.onEnable();
      this.selectionContext.attach(this.pushBasePlane);
      this.subtool.activate();
      this.pushBasePlane();
    }

    protected onDisable(): void {
      this.subtool.deactivate();
      this.selectionContext.detach();
      super.onDisable();
    }

    dispose(): void {
      this.subtool.dispose();
      super.dispose();
    }

    // ─── Undo/Redo delegation ───────────────────────────────────────────
    // The toolbar's undo/redo buttons (and the keyboard path) look for an
    // ENABLED tool whose `undoRedo` is truthy, then call its performUndo/Redo.
    // The host is the enabled tool but the undo stack lives on the subtool, so
    // expose the subtool's manager + delegate the actions to it.

    get undoRedo(): ToolUndoRedoManager | null {
      return this.subtool.undoRedo ?? null;
    }

    performUndo(): boolean {
      return this.subtool.performUndo?.() ?? false;
    }

    performRedo(): boolean {
      return this.subtool.performRedo?.() ?? false;
    }

    /** Push the current base plane to the subtool. Re-callable by the host. */
    protected pushBasePlane = (): void => {
      this.subtool.setBasePlane(this.selectionContext.getBasePlaneState());
    };

    /** Suppress / re-enable an input kind for the subtool (host gesture ordering). */
    protected muteInput(kind: SubToolInputKind, mute: boolean): void {
      if (mute) this.mutedInput.add(kind);
      else this.mutedInput.delete(kind);
    }

    private route(kind: SubToolInputKind, run: (s: ISubTool) => boolean | void): void {
      if (this.mutedInput.has(kind)) return;
      if (!this.subtool.getInputMask().has(kind)) return;
      run(this.subtool);
    }

    private forwardDrag(): void {
      this.subtool.setHostDragging?.(this.isDragging);
    }

    // ─── Domain intercept hooks (override in concrete hosts) ────────────
    // Return true to consume the event (the mixin then skips subtool routing).

    protected interceptMouseDown(_event: MouseEvent): boolean {
      return false;
    }
    protected interceptMouseMove(_event: MouseEvent): boolean {
      return false;
    }
    protected interceptMouseUp(_event: MouseEvent): boolean {
      return false;
    }
    protected interceptKeyDown(_event: KeyboardEvent): boolean {
      return false;
    }

    // ─── Input handlers (BaseTool binds these because they exist here) ──

    onMouseDown(event: MouseEvent): void {
      this.forwardDrag();
      if (this.interceptMouseDown(event)) return;
      this.route('pointer', (s) => s.onMouseDown?.(event));
    }

    onMouseMove(event: MouseEvent): void {
      this.forwardDrag();
      if (this.interceptMouseMove(event)) return;
      this.route('pointer', (s) => s.onMouseMove?.(event));
    }

    onMouseUp(event: MouseEvent): void {
      this.forwardDrag();
      if (this.interceptMouseUp(event)) return;
      this.route('pointer', (s) => s.onMouseUp?.(event));
    }

    onClick(event: MouseEvent): void {
      this.route('click', (s) => s.onClick?.(event));
    }

    onDoubleClick(event: MouseEvent): void {
      this.route('click', (s) => s.onDoubleClick?.(event));
    }

    onRightClick(event: MouseEvent): void {
      this.route('pointer', (s) => s.onRightClick?.(event));
    }

    onKeyDown(event: KeyboardEvent): void {
      if (this.interceptKeyDown(event)) return;
      // Ctrl+Z / Ctrl+Y route to the subtool's undo stack (if it has one).
      if ((event.ctrlKey || event.metaKey) && (event.key === 'z' || event.key === 'Z')) {
        if (event.shiftKey) this.subtool.performRedo?.();
        else this.subtool.performUndo?.();
        event.preventDefault();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || event.key === 'Y')) {
        this.subtool.performRedo?.();
        event.preventDefault();
        return;
      }
      this.route('keyboard', (s) => s.onKeyDown?.(event));
    }

    onKeyUp(event: KeyboardEvent): void {
      this.route('keyboard', (s) => s.onKeyUp?.(event));
    }

    onWheel(event: WheelEvent): void {
      this.route('wheel', (s) => s.onWheel?.(event));
    }

    // Drag callbacks (BaseTool detects the drag on the host; forward to the
    // subtool so it can edit existing geometry — vertex/edge drag). Gated on the
    // 'pointer' mask so a host gesture (e.g. surface align) can suppress them.
    onDragStart(data: DragEventData): void {
      this.route('pointer', (s) => s.onDragStart?.(data));
    }

    onDrag(data: DragEventData): void {
      this.route('pointer', (s) => s.onDrag?.(data));
    }

    onDragEnd(data: DragEventData): void {
      this.route('pointer', (s) => s.onDragEnd?.(data));
    }

    // ─── View & settings delegate to the subtool / adapter ──────────────

    getViewConfig(): IViewConfig {
      return this.kindAdapter.getViewConfig();
    }

    getSettingsSchema(): ToolSettingsSchema {
      return this.subtool.getSettingsSchema?.() ?? {};
    }

    onSettingsChange(event: ToolSettingsChangeEvent): void {
      this.subtool.onSettingsChange?.(event);
    }
  }

  return SubToolHostMixin;
}
